import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { HeartHandshake, Upload, Download, FileText, Loader2, CheckCircle2, Award } from "lucide-react";
import { toast } from "sonner";

const AM_GREEN = "#4A7C59";
const PM_BLUE = "#1F3A5F";

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="font-body text-sm font-medium text-[#1A2024]">{label}</label>
      {hint && <p className="font-body text-xs text-[#8A96A0]">{hint}</p>}
      <div className="mt-1">{children}</div>
    </div>
  );
}

function ApplyForm() {
  const { t } = useI18n();
  const [form, setForm] = useState({ name: "", email: "", organization: "", scope: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/helpers/apply", form);
      setDone(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Error");
    }
    setBusy(false);
  };

  if (done) {
    return (
      <div className="rounded-lg border border-[#4A7C59]/30 bg-[#4A7C59]/5 p-6 text-center" data-testid="helper-applied">
        <CheckCircle2 size={28} className="mx-auto text-[#4A7C59]" />
        <p className="mt-3 font-body text-sm text-[#2E3A46]">
          {done.already ? t(`help.status_${done.status}`) : t("help.applied")}
        </p>
        <p className="mt-2 font-body text-xs text-[#6B7F82]">{t("help.applied_next")}</p>
      </div>
    );
  }
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="helper-apply-form">
      <Field label={t("help.name")}>
        <Input required value={form.name} onChange={set("name")} className="h-11" data-testid="helper-name-input" />
      </Field>
      <Field label={t("help.email")}>
        <Input required type="email" value={form.email} onChange={set("email")} className="h-11" data-testid="helper-email-input" />
      </Field>
      <Field label={t("help.org")}>
        <Input value={form.organization} onChange={set("organization")} className="h-11" data-testid="helper-org-input" />
      </Field>
      <Field label={t("help.scope")} hint={t("help.scope_hint")}>
        <Input value={form.scope} onChange={set("scope")} className="h-11" data-testid="helper-scope-input" />
      </Field>
      <Button type="submit" disabled={busy} className="h-11 w-full bg-[#2E3A46] text-white hover:bg-[#1A2024]" data-testid="helper-apply-btn">
        {busy ? <Loader2 size={16} className="mr-2 animate-spin" /> : <HeartHandshake size={16} className="mr-2" />}
        {t("help.apply_btn")}
      </Button>
    </form>
  );
}

function UploadForm() {
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [kind, setKind] = useState("post_mortem");
  const [sheets, setSheets] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const setPhotos = (i, list) =>
    setSheets((prev) => prev.map((s, idx) => (idx === i ? { ...s, photos: list } : s)));

  const submit = async (e) => {
    e.preventDefault();
    if (!sheets.length) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("email", email);
      fd.append("code", code);
      fd.append("kind", kind);
      sheets.forEach((s, i) => {
        fd.append("files", s.file);
        s.photos.forEach((p) => fd.append(`photos_${i}`, p));
      });
      const { data } = await api.post("/helpers/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDone(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Upload failed");
    }
    setBusy(false);
  };

  if (done) {
    const st = done.stats || {};
    return (
      <div className="rounded-lg border border-[#4A7C59]/30 bg-[#4A7C59]/5 p-6 text-center" data-testid="helper-uploaded">
        <CheckCircle2 size={28} className="mx-auto text-[#4A7C59]" />
        <p className="mt-3 font-body text-sm text-[#2E3A46]">{t("help.uploaded")}</p>
        <p className="mt-2 font-body text-xs text-[#5C6873]" data-testid="helper-thanks-stats">
          {t("help.thanks_stats").replace("{total}", st.total ?? 0).replace("{approved}", st.approved ?? 0)}
        </p>
        {(done.corrected ?? 0) > 0 && (
          <p className="mx-auto mt-2 max-w-md rounded-md border border-[#C99B4B]/40 bg-[#C99B4B]/10 p-2 font-body text-xs text-[#8a6a2f]" data-testid="helper-kind-corrected">
            {t("help.kind_corrected").replace("{n}", done.corrected)}
          </p>
        )}
        {(st.approved ?? 0) >= 5 && (
          <div className="mx-auto mt-3 flex max-w-md items-center gap-2 rounded-lg border border-[#C99B4B]/40 bg-[#C99B4B]/10 p-3 text-left" data-testid="helper-thanks-milestone">
            <Award size={20} className="shrink-0 text-[#C99B4B]" />
            <p className="font-body text-xs font-medium text-[#8a6a2f]">
              {t("help.thanks_milestone").replace("{approved}", st.approved)}
            </p>
          </div>
        )}
        <Button variant="outline" className="mt-4 border-[#E2E8F0]" onClick={() => { setDone(false); setSheets([]); }} data-testid="helper-upload-again">
          {t("help.upload_more")}
        </Button>
      </div>
    );
  }
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="helper-upload-form">
      <div className="rounded-lg border border-[#E2E8F0] bg-[#F8F9FA] p-4">
        <p className="font-body text-sm font-medium text-[#1A2024]">{t("help.templates")}</p>
        <div className="mt-2 flex flex-wrap gap-2">
          <a href="/eurofind-am-template.pdf" download data-testid="tpl-am-download">
            <Button type="button" variant="outline" className="border-[#4A7C59]/40 text-[#4A7C59] hover:bg-[#4A7C59]/5 hover:text-[#4A7C59]">
              <Download size={14} className="mr-2" /> {t("help.tpl_am")}
            </Button>
          </a>
          <a href="/eurofind-pm-template.pdf" download data-testid="tpl-pm-download">
            <Button type="button" variant="outline" className="border-[#1F3A5F]/40 text-[#1F3A5F] hover:bg-[#1F3A5F]/5 hover:text-[#1F3A5F]">
              <Download size={14} className="mr-2" /> {t("help.tpl_pm")}
            </Button>
          </a>
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={t("help.email")}>
          <Input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="h-11" data-testid="upload-email-input" />
        </Field>
        <Field label={t("help.code")} hint="EF-H-0000">
          <Input required value={code} onChange={(e) => setCode(e.target.value)} className="h-11" data-testid="upload-code-input" />
        </Field>
      </div>
      <Field label={t("help.kind")}>
        <div className="grid gap-2 sm:grid-cols-2">
          {[
            { v: "ante_mortem", label: t("help.kind_am"), color: AM_GREEN },
            { v: "post_mortem", label: t("help.kind_pm"), color: PM_BLUE },
          ].map((o) => (
            <button
              key={o.v} type="button" onClick={() => setKind(o.v)}
              className="rounded-md border px-4 py-3 text-left font-body text-sm font-medium transition-base"
              style={kind === o.v
                ? { borderColor: o.color, background: `${o.color}14`, color: o.color }
                : { borderColor: "#E2E8F0", color: "#5C6873" }}
              data-testid={`upload-kind-${o.v}`}
            >
              {o.label}
            </button>
          ))}
        </div>
      </Field>
      <Field label={t("help.files")}>
        <Input type="file" accept=".pdf" multiple className="h-11 pt-2" data-testid="upload-files-input"
          onChange={(e) => setSheets(Array.from(e.target.files || []).slice(0, 10).map((f) => ({ file: f, photos: [] })))} />
      </Field>
      {sheets.length > 0 && (
        <div className="space-y-3" data-testid="sheet-list">
          {sheets.map((s, i) => (
            <div key={`${s.file.name}-${i}`} className="rounded-lg border p-3"
              style={{ borderColor: kind === "ante_mortem" ? `${AM_GREEN}55` : `${PM_BLUE}44` }}
              data-testid={`sheet-item-${i}`}>
              <div className="flex items-center gap-2">
                <FileText size={14} style={{ color: kind === "ante_mortem" ? AM_GREEN : PM_BLUE }} />
                <span className="min-w-0 flex-1 truncate font-body text-sm font-medium text-[#1A2024]">{s.file.name}</span>
                {s.photos.length > 0 && (
                  <span className="rounded-full bg-[#2E3A46]/8 px-2 py-0.5 font-body text-[11px] text-[#2E3A46]">
                    {s.photos.length} {t("help.photos_count")}
                  </span>
                )}
              </div>
              <div className="mt-2">
                <label className="font-body text-xs font-medium text-[#5C6873]">{t("help.photos")}</label>
                <Input type="file" accept="image/*" multiple className="mt-1 h-10 pt-1.5 text-xs" data-testid={`sheet-photos-input-${i}`}
                  onChange={(e) => setPhotos(i, Array.from(e.target.files || []).slice(0, 6))} />
                {s.photos.length > 0 && (
                  <p className="mt-1 truncate font-body text-xs text-[#6B7F82]">{s.photos.map((f) => f.name).join(", ")}</p>
                )}
              </div>
            </div>
          ))}
          <p className="font-body text-xs text-[#8A96A0]">{t("help.photos_hint")}</p>
        </div>
      )}
      <Button type="submit" disabled={busy || !sheets.length} className="h-11 w-full text-white"
        style={{ background: kind === "ante_mortem" ? AM_GREEN : PM_BLUE }} data-testid="helper-upload-btn">
        {busy ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Upload size={16} className="mr-2" />}
        {t("help.upload_btn")}
      </Button>
    </form>
  );
}

export default function ContributePage() {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") === "apply" ? "apply" : "upload");
  return (
    <div className="mx-auto max-w-2xl px-6 py-12" data-testid="contribute-page">
      <div className="text-center">
        <HeartHandshake size={30} className="mx-auto text-[#2E3A46]" />
        <h1 className="mt-3 font-heading text-3xl font-semibold text-[#1A2024]">{t("help.title")}</h1>
        <p className="mx-auto mt-2 max-w-xl font-body text-sm text-[#5C6873]">{t("help.subtitle")}</p>
      </div>
      <div className="mt-8 grid grid-cols-2 gap-2 rounded-lg border border-[#E2E8F0] bg-[#F8F9FA] p-1">
        {[
          { v: "apply", label: t("help.apply_tab") },
          { v: "upload", label: t("help.upload_tab") },
        ].map((o) => (
          <button key={o.v} onClick={() => setTab(o.v)}
            className={`rounded-md px-3 py-2 font-body text-sm font-medium transition-base ${tab === o.v ? "bg-white text-[#1A2024] shadow-sm" : "text-[#5C6873]"}`}
            data-testid={`contribute-tab-${o.v}`}>
            {o.label}
          </button>
        ))}
      </div>
      <div className="mt-6 rounded-lg border border-[#E2E8F0] bg-white p-6 shadow-sm">
        {tab === "apply" ? <ApplyForm /> : <UploadForm />}
      </div>
      <div className="mt-6 rounded-lg border border-[#E2E8F0] bg-[#F8F9FA] p-5">
        <div className="flex items-center gap-2 font-body text-sm font-medium text-[#1A2024]">
          <FileText size={15} /> {t("help.how_title")}
        </div>
        <ol className="mt-2 list-decimal space-y-1 pl-5 font-body text-sm text-[#5C6873]">
          <li>{t("help.how1")}</li>
          <li>{t("help.how2")}</li>
          <li>{t("help.how3")}</li>
          <li>{t("help.how_photos")}</li>
          <li>{t("help.how4")}</li>
        </ol>
      </div>
    </div>
  );
}
